import React, { useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

import Button, { ButtonTypes } from "../../../../../components/Button";
import ActionsDropdown from "../../../../../components/Dropdown/ActionsDropdown";
import { DropdownAction } from "../../../../../components/Dropdown/types";
import { KebabButton } from "../../../../../components/KebabButton";
import {
  priceUpdatedFor,
  VALUATION_PLACEHOLDER,
  valuationMessageFor,
} from "../../../../../services/valuation";
import { formatCurrency } from "../../../../../utils";
import { TRACKING_MODE_LABELS } from "../../../constants";
import {
  accountSnapshotOf,
  accountSubtitleOf,
  currencyOrDash,
  coverageGapOf,
  isLinkedSettlement,
  pricedCoverageCaption,
} from "../../../utils";
import { InvestmentActionSheet } from "../InvestmentActionSheet";
import { LedgerHeaderProps } from "./types";
import {
  Breakdown,
  BreakdownItem,
  DesktopActions,
  Figure,
  FigureCaption,
  FigureLabel,
  Figures,
  GainChip,
  GainRow,
  HeadlineAmount,
  Header,
  HeadlinePriceNote,
  Identity,
  KebabWrapper,
  ModeBadge,
  Name,
  SecondaryAmount,
  SnapshotBody,
  StatusBadge,
  Subtitle,
  TitleRow,
  Topline,
} from "./styles";

const NET_GAIN_CAPTION =
  "Broker value − net contributions + tax withheld · before income tax";
const EMPTY_NET_GAIN_CAPTION = "Begins after the first trade";
const WITHHELD_NET_GAIN_CAPTION = "Requires a complete broker value";

const LedgerHeaderComponent: React.FC<LedgerHeaderProps> = ({
  investment,
  summary,
  isEmptyAccount,
  lastUpdated,
  dropdownActions,
  onAddProgress,
  onRecordTrade,
  onMoveCash,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  // Move cash is ledger-only, so it joins the overflow here rather than the list the manual hero shares.
  const isLinked = isLinkedSettlement(investment);
  const { settlementAccountName } = investment;
  const overflowActions = useMemo<DropdownAction[]>(
    () => [
      ...(isLinked
        ? []
        : [
            {
              id: "move-cash",
              label: "Move cash",
              icon: "",
              onClick: onMoveCash,
              show: true,
            },
          ]),
      ...dropdownActions,
    ],
    [dropdownActions, onMoveCash, isLinked],
  );

  const { presentedValue, isPriced, valueText } = accountSnapshotOf(investment);
  const valueLabel = isPriced
    ? "Broker value"
    : valuationMessageFor(investment.valuation);
  const hasCurrentValuation = isPriced && investment.netGain !== null;
  const isLoss = investment.netGain !== null && investment.netGain < 0;
  const isNegativeValue = presentedValue !== null && presentedValue < 0;
  const gainText = hasCurrentValuation
    ? `${isLoss ? "-" : "+"}${formatCurrency(Math.abs(investment.netGain ?? 0))}`
    : null;

  const netGainCaption = isEmptyAccount
    ? EMPTY_NET_GAIN_CAPTION
    : gainText === null
      ? WITHHELD_NET_GAIN_CAPTION
      : NET_GAIN_CAPTION;
  const coverageGapSummary = coverageGapOf(summary);

  const statusText = investment.isActive ? "Active" : "Deactivated";
  // The ledger books every figure in euros, so the currency is fixed rather than read per account.
  const identityText = [investment.institution, "EUR"]
    .filter(Boolean)
    .join(" · ");

  const subtitleText = accountSubtitleOf(investment, lastUpdated);

  return (
    <Header>
      <KebabWrapper>
        <KebabButton
          aria-label="Investment actions"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
        />
      </KebabWrapper>
      <InvestmentActionSheet
        name={investment.name}
        subtitle={subtitleText}
        isOpen={menuOpen}
        dropdownActions={dropdownActions}
        onClose={() => setMenuOpen(false)}
        onAddProgress={onAddProgress}
        onAddHolding={onRecordTrade}
        onMoveCash={isLinked ? undefined : onMoveCash}
        trackingMode={investment.trackingMode}
      />

      <Topline>
        <Identity>
          <TitleRow>
            <Name>{investment.name}</Name>
            <StatusBadge $isActive={investment.isActive}>
              {statusText}
            </StatusBadge>
            <ModeBadge>
              {TRACKING_MODE_LABELS[investment.trackingMode]}
            </ModeBadge>
            {isLinked && settlementAccountName && (
              <ModeBadge>Paid from {settlementAccountName}</ModeBadge>
            )}
          </TitleRow>
          <Subtitle data-testid="investment-subtitle">{identityText}</Subtitle>
        </Identity>
        <DesktopActions>
          <Button
            type={ButtonTypes.primary}
            buttonType="button"
            onClick={onRecordTrade}
            aria-label="Record trade"
          >
            <AddIcon />
            Record trade
          </Button>
          <ActionsDropdown
            dropdownActions={overflowActions}
            testId="investment-hero-actions"
            compact
          />
        </DesktopActions>
      </Topline>

      <SnapshotBody>
        <Figures>
          <Figure>
            <FigureLabel>{valueLabel}</FigureLabel>
            <HeadlineAmount
              $negative={isNegativeValue}
              data-testid="investment-value"
            >
              {valueText}
            </HeadlineAmount>
            <HeadlinePriceNote
              priceUpdated={isPriced ? priceUpdatedFor(investment) : null}
            />
          </Figure>
          <Figure>
            <FigureLabel>Net gain</FigureLabel>
            <GainRow>
              {gainText === null ? (
                <SecondaryAmount data-testid="investment-gain">
                  {VALUATION_PLACEHOLDER}
                </SecondaryAmount>
              ) : (
                <GainChip $negative={isLoss} data-testid="investment-gain">
                  {isLoss ? <TrendingDownIcon /> : <TrendingUpIcon />}
                  {gainText}
                </GainChip>
              )}
            </GainRow>
            <FigureCaption>{netGainCaption}</FigureCaption>
          </Figure>
        </Figures>
        <Breakdown>
          {!isLinked && (
            <BreakdownItem>
              <dt>Cash</dt>
              <dd>
                {currencyOrDash(
                  coverageGapSummary
                    ? coverageGapSummary.cashBalance
                    : investment.cashBalance,
                )}
              </dd>
            </BreakdownItem>
          )}
          {coverageGapSummary ? (
            <BreakdownItem>
              <dt>Known securities subtotal</dt>
              <dd>{formatCurrency(coverageGapSummary.knownSecuritiesValue)}</dd>
              <dd>{pricedCoverageCaption(coverageGapSummary)}</dd>
            </BreakdownItem>
          ) : (
            <BreakdownItem>
              <dt>Securities value</dt>
              <dd>{currencyOrDash(investment.securitiesValue)}</dd>
            </BreakdownItem>
          )}
        </Breakdown>
      </SnapshotBody>
    </Header>
  );
};

export const LedgerHeader = React.memo(LedgerHeaderComponent);
